import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { Add, Search, Cancel } from "@mui/icons-material";
import ConfirmDialog from "../components/ConfirmDialog";
import {
  fetchAllUsers,
  fetchUserStats,
  createNewUser,
  updateUser,
  removeUser,
  clearSuccessMessage,
  clearError,
} from "../redux/slices/userManagementSlice";
import {
  UsersTable,
  AddUserDialog,
  EditUserDialog,
  UserStatsCards,
  UserDetailsDialog,
} from "../components/users";
import { isAdmin, USER_ROLES } from "../utils/roleUtils";
import { SPACING, ICON_SIZES } from "../theme/constants";
import { flexBetween } from "../theme/responsive";

export const Users = ({ plans = [] }) => {
  const dispatch = useDispatch();
  const {
    users,
    stats,
    pagination,
    loading,
    statsLoading,
    error,
    successMessage,
  } = useSelector((state) => state.userManagement);

  const { user: currentUser } = useSelector((state) => state.user);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    role: "",
    status: "",
    page: 1,
    limit: 10,
  });

  // Fetch users and stats on mount and when filters change
  useEffect(() => {
    if (isAdmin(currentUser)) {
      dispatch(fetchAllUsers(filters));
      dispatch(fetchUserStats());
    }
  }, [dispatch, filters, currentUser]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleAddUser = async (userData) => {
    try {
      await dispatch(createNewUser(userData)).unwrap();
      setOpenAddDialog(false);
      dispatch(fetchAllUsers(filters));
    } catch (err) {
      console.error("Failed to add user:", err);
    }
  };

  const handleDeleteUser = async (userId) => {
    setDeleteTarget(userId);
  };

  const confirmDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(removeUser(deleteTarget)).unwrap();
      dispatch(fetchAllUsers(filters));
    } catch (err) {
      console.error("Failed to delete user:", err);
    }
    setDeleteTarget(null);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setOpenViewDialog(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setOpenEditDialog(true);
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      await dispatch(updateUser({ userId, userData })).unwrap();
      setOpenEditDialog(false);
      setSelectedUser(null);
      dispatch(fetchAllUsers(filters));
    } catch (err) {
      console.error("Failed to update user:", err);
    }
  };

  const handleSearchChange = (e) => {
    setFilters({ ...filters, search: e.target.value, page: 1 });
  };

  const handleRoleFilter = (e) => {
    setFilters({ ...filters, role: e.target.value, page: 1 });
  };

  const handleStatusFilter = (e) => {
    setFilters({ ...filters, status: e.target.value, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleLimitChange = (newLimit) => {
    setFilters({ ...filters, limit: newLimit, page: 1 });
  };

  // Check if user is admin
  const userIsAdmin = isAdmin(currentUser);

  if (!userIsAdmin) {
    return (
      <Box sx={{ textAlign: "center", mt: SPACING.xxl }}>
        <Cancel
          sx={{ fontSize: ICON_SIZES.xxl, color: "error.main", mb: SPACING.sm }}
        />
        <Typography variant="h4" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You don't have permission to access this page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          ...flexBetween,
          mb: SPACING.lg,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: SPACING.xs }}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage user accounts, roles, and permissions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenAddDialog(true)}
        >
          Add User
        </Button>
      </Box>

      {/* Messages */}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => dispatch(clearSuccessMessage())}
        >
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => dispatch(clearError())}
        >
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && <UserStatsCards stats={stats} loading={statsLoading} />}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or email..."
                value={filters.search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role}
                  label="Role"
                  onChange={handleRoleFilter}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value={USER_ROLES.USER}>User</MenuItem>
                  <MenuItem value={USER_ROLES.ADMIN}>Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 3.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={handleStatusFilter}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <UsersTable
          users={users}
          pagination={pagination}
          onDeleteUser={handleDeleteUser}
          onEditUser={handleEditUser}
          onViewUser={handleViewUser}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          currentUser={currentUser}
        />
      )}

      {/* Dialogs */}
      <UserDetailsDialog
        open={openViewDialog}
        onClose={() => {
          setOpenViewDialog(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onEdit={handleEditUser}
      />

      <AddUserDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onAddUser={handleAddUser}
        currentUserRole={currentUser?.role}
      />

      {selectedUser && (
        <EditUserDialog
          open={openEditDialog}
          onClose={() => {
            setOpenEditDialog(false);
            setSelectedUser(null);
          }}
          onUpdateUser={handleUpdateUser}
          user={selectedUser}
          currentUserRole={currentUser?.role}
          plans={plans}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete"
        confirmColor="error"
      />
    </Box>
  );
};

export default Users;
